import { useEffect, useState } from "react";
import { useAppKitConnection } from "@reown/appkit-adapter-solana/react";
import { useAppKitAccount, useAppKitBalance } from "@reown/appkit/react";
import { Coins } from "ton3-core";
import { useBalance } from "wagmi";
import {
  getAccount,
  getAssociatedTokenAddress,
  getMint,
} from "@solana/spl-token";
import { PublicKey } from "@solana/web3.js";
import { useTonAddress, useTonWallet } from "@tonconnect/ui-react";
import {
  useGetAssetsQuery,
  useGetBalancesQuery,
} from "../store/api/dexApiSlice";

export interface PairData {
  token0Address?: string;
  token1Address?: string;
  isLoading?: boolean;
  address?: string;
  chainRouter?: "Avalanche" | "Solana" | "Toncoin";
}

export const useUnifiedPairBalances = (data: PairData) => {
  const [token0Balance, setToken0Balance] = useState<Coins | number>(0);
  const [token1Balance, setToken1Balance] = useState<Coins | number>(0);
  const [nativeTokenBalance, setNativeTokenBalance] = useState<number>(0);

  // === EVM ===
  const isEvmToken = (token?: string): token is `0x${string}` =>
    typeof token === "string" && token.startsWith("0x");
  const NATIVE_TOKEN_AVAX = "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee";
  const nativeBalanceEVM = useBalance({
    address: data?.address as `0x${string}`,
    query: { refetchInterval: 5000 },
  });
  const balance0EVM = useBalance({
    address: data?.address as `0x${string}`,
    token:
      isEvmToken(data?.token0Address) &&
      data?.token0Address?.toLowerCase() !== NATIVE_TOKEN_AVAX
        ? data.token0Address
        : undefined,
    query: { refetchInterval: 5000 },
  });
  const balance1EVM = useBalance({
    address: data?.address as `0x${string}`,
    token:
      isEvmToken(data?.token1Address) &&
      data?.token1Address?.toLowerCase() !== NATIVE_TOKEN_AVAX
        ? data.token1Address
        : undefined,
    query: { refetchInterval: 5000 },
  });

  // === Solana ===
  const { connection }: any = useAppKitConnection();
  const { address: solanaAddress, isConnected: solanaConnected } =
    useAppKitAccount();
  const { fetchBalance } = useAppKitBalance();

  const getSolanaTokenBalance = async (
    mintAddress: string,
    owner: string
  ): Promise<number> => {
    try {
      const TOKEN_MINT = new PublicKey(mintAddress);
      const ownerPK = new PublicKey(owner);
      const tokenAccountAddress = await getAssociatedTokenAddress(
        TOKEN_MINT,
        ownerPK
      );
      const tokenAccount = await getAccount(connection, tokenAccountAddress);
      const mintInfo = await getMint(connection, TOKEN_MINT);
      return Number(tokenAccount.amount) / Math.pow(10, mintInfo.decimals);
    } catch {
      return 0;
    }
  };

  const getSolanaBalances = async () => {
    if (!solanaAddress || !connection) return;
    const solNativeBalance: any = await fetchBalance();
    setNativeTokenBalance(Number(solNativeBalance?.data.balance || 0));
    if (data.token0Address === "SoNative11111111111111111111111111111111111") {
      const balance: any = await fetchBalance();
      setToken0Balance(Number(balance?.data.balance || 0));
    } else {
      const balance = await getSolanaTokenBalance(
        data.token0Address!,
        solanaAddress
      );
      setToken0Balance(balance);
    }

    if (data.token1Address === "SoNative11111111111111111111111111111111111") {
      const balance: any = await fetchBalance();
      setToken1Balance(Number(balance?.data.balance || 0));
    } else {
      const balance = await getSolanaTokenBalance(
        data.token1Address!,
        solanaAddress
      );
      setToken1Balance(balance);
    }
  };

  // === Ton ===
  const wallet = useTonWallet();
  const walletAddress = useTonAddress();
  const { data: assets } = useGetAssetsQuery();
  const { data: balances } = useGetBalancesQuery(walletAddress, {
    pollingInterval: 20000,
    skip: !wallet,
  });

  const getTonBalance = (tokenAddress?: string): Coins => {
    if (!balances || !assets || !tokenAddress) return Coins.fromNano(0, 9);
    const balance = balances[tokenAddress] ?? 0;
    const decimals = assets[tokenAddress]?.decimals ?? 9;
    return Coins.fromNano(balance, decimals);
  };

  // === Effects ===
  useEffect(() => {
    if (data.chainRouter === "Solana" && solanaConnected) {
      const interval = setInterval(getSolanaBalances, 5000);
      return () => clearInterval(interval);
    }
  }, [data.token0Address, data.token1Address, solanaAddress, solanaConnected]);

  useEffect(() => {
    if (data.chainRouter === "Avalanche") {
      setToken0Balance(Number(balance0EVM?.data?.formatted || 0));
      setToken1Balance(Number(balance1EVM?.data?.formatted || 0));
      setNativeTokenBalance(Number(nativeBalanceEVM?.data?.formatted || 0));
    }
  }, [balance0EVM.data, balance1EVM.data]);

  useEffect(() => {
    if (data.chainRouter === "Toncoin" && wallet) {
      setToken0Balance(getTonBalance(data.token0Address));
      setToken1Balance(getTonBalance(data.token1Address));

      // Token native
      const tonBalance = getTonBalance(
        "EQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAM9c"
      );
      setNativeTokenBalance(Number(tonBalance));
    }
  }, [balances, assets, data.token0Address, data.token1Address]);

  useEffect(() => {
    if (
      (data.chainRouter === "Solana" && !solanaConnected) ||
      (data.chainRouter === "Toncoin" && !wallet)
    ) {
      setToken0Balance(0);
      setToken1Balance(0);
    }
  }, [solanaConnected, wallet]);

  return {
    token0Balance,
    token1Balance,
    nativeTokenBalance,
    isLoading: data.isLoading,
    address: data.address,
    chainRouter: data.chainRouter,
  };
};
